/* eslint-disable no-void */

import * as vp from 'vopay';

import { useEffect, useState } from 'react';
import axios from 'axios';

import vopayApi from '../../services/api.config';

type HookProps = {
  api: vp.Api;
};

export type IQ11LoginRequest = {
  EmailAddress: string;
  Password: string;
};

export type IQ11LoginResponse = {
  success: boolean;
  errorMessage: string;
  clientAccount: {
    BusinessName: string;
    ClientAccountID: string;
    ClientDisplayName: string;
    ClientType: string;
    DateAdded: string;
    DateDeleted: string;
    EmailAddress: string;
    FirstName: string;
    LastName: string;
    IPAddress: string;
    IsActive: string;
    IsPrimaryAccount: string;
    LastModified: string;
    TransactionLimit: string;
    ID: number;
    VIOClientAccountUsers_ID: number;
    VIOAccounts_ID: number;
    IsBillingAccount: string;
  };
  permissions: vp.IQ11Permissions;
  jwt: string;
};

type ReturnTuple = [IQ11LoginResponse, (requestParams: IQ11LoginRequest) => void, () => void];

function usePostIQ11Login({ api }: HookProps): ReturnTuple {
  const source = axios.CancelToken.source();

  const [params, setParams] = useState<IQ11LoginRequest>();
  const [response, setResponse] = useState<IQ11LoginResponse>({
    success: false,
    errorMessage: '',
    clientAccount: {
      BusinessName: '',
      ClientAccountID: '',
      ClientDisplayName: '',
      ClientType: '',
      DateAdded: '',
      DateDeleted: '',
      EmailAddress: '',
      FirstName: '',
      LastName: '',
      IPAddress: '',
      IsActive: '',
      IsPrimaryAccount: '',
      LastModified: '',
      TransactionLimit: '',
      ID: 0,
      VIOClientAccountUsers_ID: 0,
      VIOAccounts_ID: 0,
      IsBillingAccount: '',
    },
    permissions: {
      BankAccountsEnabled: false,
      CreditCardsEnabled: false,
    },
    jwt: '',
  });
  const [shouldSendRequest, setSendRequest] = useState(false);

  useEffect(() => {
    const sendRequest = async (requestParams: IQ11LoginRequest) => {
      const { Success, ErrorMessage, ClientAccount, JWT, Permissions } = await api.postIQ11Login(
        buildParams(requestParams),
        source,
      );

      const res = {
        success: Success,
        errorMessage: ErrorMessage || '',
        clientAccount: ClientAccount || {},
        jwt: JWT || '',
        permissions: Permissions,
      };

      setResponse(res);
      setSendRequest(false);

      return () => {};
    };

    if (params && shouldSendRequest) void sendRequest(params);

    return () => {
      source.cancel();
    };
  }, [shouldSendRequest]);

  return [
    response,
    (requestParams: IQ11LoginRequest) => {
      setParams(requestParams);
      setSendRequest(true);
    },
    () => {
      setResponse({
        success: false,
        errorMessage: '',
        clientAccount: {
          BusinessName: '',
          ClientAccountID: '',
          ClientDisplayName: '',
          ClientType: '',
          DateAdded: '',
          DateDeleted: '',
          EmailAddress: '',
          FirstName: '',
          LastName: '',
          IPAddress: '',
          IsActive: '',
          IsPrimaryAccount: '',
          LastModified: '',
          TransactionLimit: '',
          ID: 0,
          VIOClientAccountUsers_ID: 0,
          VIOAccounts_ID: 0,
          IsBillingAccount: '',
        },
        permissions: {
          BankAccountsEnabled: false,
          CreditCardsEnabled: false,
        },
        jwt: '',
      });
      setSendRequest(false);
    },
  ];

  function buildParams(requestParams: IQ11LoginRequest): vp.IQ11Login {
    return {
      EmailAddress: requestParams.EmailAddress,
      Password: requestParams.Password,
    };
  }
}

export default vopayApi.withApiHook<HookProps, ReturnTuple>(usePostIQ11Login);
